
// Enhanced input validation and sanitization utilities with XSS prevention

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  if (username.length < 3) {
    return { isValid: false, error: "Username must be at least 3 characters long" };
  }
  if (username.length > 30) {
    return { isValid: false, error: "Username must be 30 characters or less" };
  }
  
  // Allow only alphanumeric characters, underscores, and hyphens
  const usernameRegex = /^[a-zA-Z0-9_-]+$/;
  if (!usernameRegex.test(username)) {
    return { isValid: false, error: "Username can only contain letters, numbers, underscores, and hyphens" };
  }
  
  return { isValid: true };
};

export const sanitizeInput = (input: string): string => {
  // Enhanced sanitization to prevent XSS attacks
  return input
    .replace(/[<>\"'&]/g, '') // Remove HTML/script injection characters
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .trim();
};

export const sanitizeHtml = (input: string): string => {
  // More comprehensive HTML sanitization
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/&/g, '&amp;')
    .trim();
};

export const validateDisplayName = (name: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(name);
  if (sanitized.length < 1) {
    return { isValid: false, error: "Display name is required" };
  }
  if (sanitized.length > 50) {
    return { isValid: false, error: "Display name must be 50 characters or less" };
  }
  // Check for potentially malicious patterns
  if (/script|javascript|vbscript|onload|onerror|onclick/i.test(sanitized)) {
    return { isValid: false, error: "Display name contains invalid characters" };
  }
  return { isValid: true };
};

export const validateBio = (bio: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(bio);
  if (sanitized.length > 500) {
    return { isValid: false, error: "Bio must be 500 characters or less" };
  }
  // Check for potentially malicious patterns
  if (/script|javascript|vbscript|onload|onerror|onclick/i.test(sanitized)) {
    return { isValid: false, error: "Bio contains invalid characters" };
  }
  return { isValid: true };
};

export const validateReportReason = (reason: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(reason);
  if (sanitized.length < 1) {
    return { isValid: false, error: "Report reason is required" };
  }
  if (sanitized.length > 200) {
    return { isValid: false, error: "Report reason must be 200 characters or less" };
  }
  return { isValid: true };
};

export const validateReportDescription = (description: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(description);
  if (sanitized.length > 1000) {
    return { isValid: false, error: "Report description must be 1000 characters or less" };
  }
  return { isValid: true };
};

export const sanitizeUrl = (url: string): string => {
  // Sanitize URLs to prevent malicious redirects
  try {
    const parsedUrl = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return '';
    }
    return parsedUrl.toString();
  } catch {
    return '';
  }
};

export const validateFileName = (fileName: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(fileName);
  if (sanitized.length > 255) {
    return { isValid: false, error: "File name too long" };
  }
  // Check for directory traversal attempts
  if (/\.\.\/|\.\.\\|\/\.\.|\\\.\./.test(fileName)) {
    return { isValid: false, error: "Invalid file name" };
  }
  return { isValid: true };
};
